import React, { useRef, useEffect, useState, useCallback } from "react";
import { TextField, InputAdornment } from "@material-ui/core";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "@/langs/useTranslation";
import { Search } from "@material-ui/icons";

// This is an example of the classic "Place Autocomplete" widget.
// https://developers.google.com/maps/documentation/javascript/place-autocomplete
export default function GoogleAutoComplete({ onPlaceSelect }) {
  const places = useMapsLibrary("places");
  const map = useMap();

  const { t } = useTranslation();

  const [showSugguest, setShowSugguest] = useState(false);
  const [sessionToken, setSessionToken] = useState();
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [predictionResults, setPredictionResults] = useState([]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!places || !map) return;

    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setAutocompleteService(null);
  }, [map, places]);

  const fetchPredictions = useCallback(
    async inputValue => {
      if (!autocompleteService || !inputValue) {
        setPredictionResults([]);
        return;
      }

      const request = { input: inputValue, sessionToken };
      const response = await autocompleteService.getPlacePredictions(request);

      setPredictionResults(response.predictions);
    },
    [autocompleteService, sessionToken]
  );

  const onInputChange = useCallback(
    event => {
      const value = event.target?.value;
      setInputValue(value);
      fetchPredictions(value);
    },
    [fetchPredictions]
  );

  const handleSuggestionClick = useCallback(
    placeId => {
      if (!places) return;

      const detailRequestOptions = {
        placeId,
        fields: ["geometry", "name", "formatted_address"],
        sessionToken
      };

      const detailsRequestCallback = placeDetails => {
        onPlaceSelect(placeDetails);
        setPredictionResults([]);
        setInputValue(placeDetails?.formatted_address ?? "");
        setSessionToken(new places.AutocompleteSessionToken());
      };

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback);
    },
    [onPlaceSelect, places, placesService, sessionToken]
  );

  return (
    <div className="autocomplete-container">
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        placeholder={t({ defaultMessage: "搜索地址" })}
        value={inputValue}
        onChange={onInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="disabled" />
            </InputAdornment>
          )
        }}
        onFocus={() => setShowSugguest(true)}
        onBlur={() => setShowSugguest(false)}
      />
      {showSugguest && predictionResults.length > 0 && (
        <ul className="suggestions-list">
          {predictionResults.map(predictionResult => (
            <li
              key={predictionResult.place_id}
              className="suggestion-item"
              onMouseDown={() => {
                handleSuggestionClick(predictionResult.place_id);
              }}
              onTouchStart={() => {
                handleSuggestionClick(predictionResult.place_id);
              }}
            >
              <span className="suggestion-name">
                {predictionResult.description}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
